import React, { useContext, useState } from 'react';
import _ from 'lodash';

import LegoAdminPageContext from '../../pages/legoAdminPageContext';
import { Icon } from './Icon';
import { IconButton } from './IconButton';
import AsyncS3Uploader from './react-s3-upload/AsyncS3Uploader';

// Same cap as MAX_OUTGOING_ATTACHMENTS_MB in model/conversations.js (why 14MB is there), checked here to skip a
// useless upload
const MAX_ATTACHMENTS_MB = 14;

// Files attached to an email conversation message: uploaded to S3 as they are dropped or picked, and sent as
// options.attachments to services/data/emails/conversations. Spread dropZoneProps on the element that takes drops.
// Each attachment carries its upload `progress` until it has a `url`, and `removing` while ✕ deletes it from S3
export function useEmailAttachments() {
  const { page } = useContext(LegoAdminPageContext);
  const [attachments, setAttachments] = useState([]);
  const [dragging, setDragging] = useState(false);
  const attachmentsService = page.service('services/s3-email-attachments');

  const change = (id, changes) => setAttachments((current) => _.map(current, (a) => (a.id === id ? { ...a, ...changes } : a)));
  const drop = (id) => setAttachments((current) => _.reject(current, { id }));

  const uploadOne = async (picked) => {
    // Browsers leave the type empty for unknown extensions, and SparkPost requires one
    const file = picked.type ? picked : new File([picked], picked.name, { type: 'application/octet-stream' });
    const id = _.uniqueId('attachment');
    setAttachments((current) => [...current, { id, fileName: file.name, contentType: file.type, size: file.size, progress: 0 }]);
    const uploader = new AsyncS3Uploader({
      getSignedUrl: (f) => attachmentsService.create({ fileName: f.name, mimeType: f.type }),
      onProgress: (progress) => change(id, { progress }),
      // No Content-Disposition, like incoming attachments (processAttachments): the admin's link opens them in a tab
      contentDisposition: null,
    });
    try {
      const { s3Url } = await uploader.uploadFile(file);
      change(id, { url: s3Url });
    } catch (err) {
      drop(id);
      alert(`Could not upload ${file.name}: ${err.message}`);
    }
  };

  const upload = (files) => {
    const totalBytes = _.sumBy([...attachments, ...files], 'size');
    if (totalBytes > MAX_ATTACHMENTS_MB * 1024 * 1024) {
      alert(`Attachments can't add up to more than ${MAX_ATTACHMENTS_MB}MB`);
      return;
    }
    files.forEach(uploadOne);
  };

  const remove = async ({ id, url }) => {
    change(id, { removing: true });
    try {
      await attachmentsService.remove(url);
      drop(id);
    } catch (err) {
      change(id, { removing: false });
      alert(`Could not delete the file: ${err.message}`);
    }
  };

  // Sent once every upload and removal has finished (see `busy`)
  const ready = _.filter(attachments, (a) => a.url && !a.removing);
  const busy = ready.length < attachments.length;

  const dropZoneProps = {
    onDrop: (e) => {
      e.preventDefault();
      setDragging(false);
      upload([...e.dataTransfer.files]);
    },
    onDragOver: (e) => {
      e.preventDefault();
      setDragging(true);
    },
    onDragLeave: () => setDragging(false),
  };

  return { attachments: ready, chips: { attachments, remove }, busy, clear: () => setAttachments([]), upload, dragging, dropZoneProps };
}

export function AttachButton({ upload }) {
  return <label className={'btn btn-link btn-sm px-1 mb-0'} title={'Attach files (or drop them on the editor)'}>
    <Icon small icon={'attach_file'}/>
    <span className={'small text-secondary ml-1'}>max {MAX_ATTACHMENTS_MB}MB</span>
    <input type={'file'} multiple hidden onChange={(e) => {
      upload([...e.target.files]);
      e.target.value = '';
    }}/>
  </label>;
}

// Takes useEmailAttachments().chips
export function AttachmentChips({ attachments, remove }) {
  if (!attachments.length) {
    return null;
  }
  return <div className={'mt-1'}>{_.map(attachments, (file) =>
    <span key={file.id} className={'badge badge-light border mr-1'}>
      {file.fileName} ({file.size >> 10}kb)
      {/* The uploader reports -1 when the browser can't compute the progress */}
      {!file.url && <span className={'text-secondary ml-1'}>{file.progress >= 0 ? `${file.progress}%` : '...'}</span>}
      {file.removing && <span className={'spinner-border spinner-border-sm ml-1'}/>}
      {file.url && !file.removing && <IconButton size={'sm'} level={'secondary'} icon={'close'} title={'Remove'}
                                                 onClick={() => remove(file)}/>}
    </span>)}
  </div>;
}
